import type { PlanUsageDto } from './schemas';
import type {
  AttachmentUsage,
  ExportUsage,
  PlanUsageModel,
  ReminderUsage,
  ScanUsage,
  UsageCounter,
} from '../model/types';

/** Compute is_limit_reached when the server omits the flag (count >= limit). */
function computeLimitReached(
  explicit: boolean | null | undefined,
  used: number | null | undefined,
  limit: number | null | undefined,
): boolean {
  if (explicit != null) return explicit;
  const u = used ?? 0;
  const l = limit ?? 0;
  return l > 0 && u >= l;
}

function toCounter(
  dto:
    | {
        count?: number | null;
        limit?: number | null;
        is_limit_reached?: boolean | null;
        days_until_reset?: number | null;
      }
    | null
    | undefined,
): UsageCounter | null {
  if (!dto) return null;
  return {
    count: dto.count ?? 0,
    limit: dto.limit ?? 0,
    isLimitReached: computeLimitReached(dto.is_limit_reached, dto.count, dto.limit),
    daysUntilReset: dto.days_until_reset ?? null,
  };
}

export function toPlanUsage(dto: PlanUsageDto): PlanUsageModel {
  const attachDto = dto.attachments;
  const exportDto = dto.exports;
  const scanDto = dto.receipt_scans;
  const remDto = dto.reminders;

  const attachments: AttachmentUsage | null = attachDto
    ? {
        weekUsed: attachDto.week_used ?? 0,
        weeklyLimit: attachDto.weekly_limit ?? 0,
        isWeeklyLimitReached: computeLimitReached(
          attachDto.is_weekly_limit_reached,
          attachDto.week_used,
          attachDto.weekly_limit,
        ),
        daysUntilReset: attachDto.days_until_reset ?? null,
        maxFileSizeMb: attachDto.max_file_size_mb ?? 3,
      }
    : null;

  const exports: ExportUsage | null = exportDto
    ? {
        monthUsed: exportDto.month_used ?? 0,
        monthlyLimit: exportDto.monthly_limit ?? 0,
        isMonthlyLimitReached: computeLimitReached(
          exportDto.is_monthly_limit_reached,
          exportDto.month_used,
          exportDto.monthly_limit,
        ),
        daysUntilReset: exportDto.days_until_reset ?? null,
      }
    : null;

  // receipt_scans: limit reached when remaining <= 0 (or explicit flag)
  const receiptScans: ScanUsage | null = scanDto
    ? {
        remaining: scanDto.remaining ?? 0,
        isLimitReached: scanDto.is_limit_reached ?? (scanDto.remaining ?? 1) <= 0,
      }
    : null;

  // reminders: server uses "combined" key (not "combined_reminders")
  const combinedRem = remDto?.combined;
  const reminders: ReminderUsage | null = remDto
    ? {
        combinedReminders: combinedRem
          ? {
              count: combinedRem.week_used ?? 0,
              limit: combinedRem.weekly_limit ?? 0,
              isLimitReached: computeLimitReached(
                combinedRem.is_limit_reached,
                combinedRem.week_used,
                combinedRem.weekly_limit,
              ),
              daysUntilReset: combinedRem.days_until_reset ?? null,
            }
          : null,
      }
    : null;

  return {
    isPro: dto.is_pro ?? false,
    groups: toCounter(dto.groups),
    wallets: toCounter(dto.wallets),
    receiptScans,
    attachments,
    exports,
    devices: toCounter(dto.devices),
    reminders,
  };
}
